import React from "react";
import {Stack} from "@mui/material";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import {ManagementRoleView} from "arteco-api-client-ts";
import {SoftChip} from "../../kit";

/**
 * Roles del sistema de gestión (ISO 9001, ISO/IEC 27001 y ENS) asignados por designación (#13896).
 * Los ven el admin (designaciones y ficha de la persona) y toda la plantilla (directorio y
 * organigrama, #13897).
 */

/** Etiquetas en el orden en que se enseñan. Coinciden con las del enum `ManagementRole` del API. */
export const MANAGEMENT_ROLES: { value: string, label: string }[] = [
	{value: "MANAGEMENT", label: "Dirección"},
	{value: "INFORMATION_OWNER", label: "Responsable de la información"},
	{value: "SERVICE_OWNER", label: "Responsable del servicio"},
	{value: "SYSTEM_OWNER", label: "Responsable del sistema"},
	{value: "SECURITY_OFFICER", label: "Responsable de seguridad"},
	{value: "QUALITY_COORDINATOR", label: "Coordinación de calidad"},
	{value: "INTERNAL_AUDITOR", label: "Auditor interno"},
	{value: "SECURITY_CONTACT", label: "Contacto de seguridad para clientes"},
];

export const managementRoleLabel = (role?: string) => MANAGEMENT_ROLES.find(r => r.value === role)?.label ?? role ?? "";

/** Un rol que tiene una persona: de titular, o de suplente (si el rol está vacante, en funciones). */
export interface PersonRole {
	role: string;
	deputy: boolean;
	/** Suplente de un rol sin titular: lo ejerce en funciones. */
	acting?: boolean;
}

const order = (role: string) => MANAGEMENT_ROLES.findIndex(r => r.value === role);

/** De las designaciones vigentes, los roles de cada persona (por id). */
export const rolesByPerson = (views?: ManagementRoleView[]): Map<number, PersonRole[]> => {
	const map = new Map<number, PersonRole[]>();
	const add = (id: number | undefined, r: PersonRole) => {
		if (id === undefined) {
			return;
		}
		map.set(id, [...(map.get(id) || []), r].sort((a, b) => order(a.role) - order(b.role)));
	};
	(views || []).forEach(v => {
		add(v.holder?.id, {role: v.role || "", deputy: false});
		add(v.deputy?.id, {role: v.role || "", deputy: true, acting: !v.holder});
	});
	return map;
};

export const personRoleLabel = (r: PersonRole) => r.deputy
	? `${managementRoleLabel(r.role)} (${r.acting ? "en funciones" : "suplente"})`
	: managementRoleLabel(r.role);

/** Distintivos de solo lectura con los roles de una persona. Sin roles, nada. */
export const RoleBadges = ({roles, justify}: { roles?: PersonRole[], justify?: "center" | "flex-start" }) => {
	if (!roles || roles.length === 0) {
		return null;
	}
	return (
		<Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent={justify}>
			{roles.map(r => (
				<SoftChip key={r.role + r.deputy} tone={r.deputy ? "neutral" : "info"} label={personRoleLabel(r)}
						  icon={r.deputy ? undefined : <VerifiedUserOutlinedIcon/>}/>
			))}
		</Stack>
	);
};
